import { useCallback, useMemo } from "react";
import {
  actions,
  useMountedLayoutEffect,
  useGetLatest,
  makePropGetter,
  ensurePluginOrder,
} from "react-table";

const pluginName = "useEditTableCell";

// Actions
actions.resetEditCell = "resetEditCell";
actions.toggleEditCell = "toggleEditCell";
actions.setFormValue = "setFormValue";
actions.updateEditCell = "updateEditCell";

export const useEditTableCell = (hooks) => {
  hooks.getEditRowCellProps = [defaultGetToggleEditRowCellsProps];

  hooks.stateReducers.push(reducer);
  hooks.useInstance.push(useInstance);
  hooks.prepareRow.push(prepareRow);
};

useEditTableCell.pluginName = pluginName;

const defaultGetToggleEditRowCellsProps = (
  props,
  { instance, row, userProps }
) => {
  const {
    state: { formValue },
    dispatch,
  } = instance;

  return [
    props,
    {
      onChange: (e) => {
        dispatch({
          type: actions.updateEditCell,
          payload: { [e.target.name]: e.target.value },
        });
      },
      value: formValue?.[userProps],
    },
  ];
};

const reducer = (state, action, previousState, instance) => {
  if (action?.type === actions?.init) {
    return {
      formValue: {},
      editingId: null,
      ...state,
    };
  }

  if (action?.type === actions?.resetEditCell) {
    return {
      ...state,
      selectedRowIds: instance.initialState.formValue || {},
    };
  }

  if (action?.type === actions?.toggleEditCell) {
    const { rowId } = action;

    return {
      ...state,
      editingId: rowId,
      // formValue: { ...state.formValue, [] }
    };
  }

  if (action.type === actions.setFormValue) {
    const { payload } = action;
    const editableHeaders = instance.headers.reduce((result, header) => {
      if (header?.editable === true && header?.id) {
        result.push(header.id);
      }
      return result;
    }, []);

    if (!payload?.original) return;

    const form = Object.entries(payload?.original)
      .filter(([key]) => editableHeaders.includes(key))
      .reduce((result, [key, value]) => ({ ...result, [key]: value }), {});

    return {
      ...state,
      formValue: form,
    };
  }

  if (action.type === actions.updateEditCell) {
    const { payload } = action;
    return {
      ...state,
      formValue: { ...state.formValue, ...payload },
    };
  }
};

const useInstance = (instance) => {
  const {
    rows,
    autoResetEditing = true,
    plugins,
    state: { filters, groupBy, sortBy, formValue, editingId },
    dispatch,
    data,
    enableEditing = false,
    rowsById,
  } = instance;

    ensurePluginOrder(
      plugins,
      [
        "useGlobalFilter",
        "useFilters",
        "useGroupBy",
        "useSortBy",
        "useExpanded",
        "usePagination",
      ],
      "useEditTableCell"
    );

  const getAutoResetEditing = useGetLatest(autoResetEditing);

  useMountedLayoutEffect(() => {
    if (getAutoResetEditing()) {
      dispatch({ type: actions.resetEditCell });
    }
  }, [dispatch, filters, groupBy, sortBy]);

  const formValues = useMemo(() => formValue, [formValue]);
  const isEditId = useMemo(() => editingId, [editingId]);

  const setEditState = useCallback(
    (rowId) => {
      if (enableEditing && rowId && rowsById[rowId]) {
        dispatch({ type: actions?.setFormValue, payload: rowsById[rowId] });
        dispatch({ type: actions?.toggleEditCell, rowId });
      }

      if (rowId === null) {
        dispatch({ type: actions?.toggleEditCell, rowId });
      }
    },
    [dispatch, rowsById]
  );

  Object.assign(instance, {
    formValues,
    isEditId,
    setEditState,
  });
};

const prepareRow = (row, { instance }) => {
  row.getEditRowCellProps = makePropGetter(
    instance.getHooks().getEditRowCellProps,
    { instance: instance, row }
  );
};
